import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Checkout.css';

const Checkout = () => {
    const navigate = useNavigate();
    const [wishlist, setWishlist] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'cod'
    const [loading, setLoading] = useState(false);

    // ✅ NEW: Address selection state
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(null);
    const [isManualAddress, setIsManualAddress] = useState(false);

    // Card details state
    const [cardDetails, setCardDetails] = useState({
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: ''
    });

    // Shipping details state
    const [shippingDetails, setShippingDetails] = useState({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: ''
    });

    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
        if (storedWishlist.length === 0) {
            navigate('/wishlist');
        }
        setWishlist(storedWishlist);
    }, [navigate]);

    useEffect(() => {
        // ✅ Load all saved addresses
        const addresses = JSON.parse(localStorage.getItem('userAddresses') || '[]');
        setSavedAddresses(addresses);

        // ✅ Auto-select default address
        const defaultAddressIndex = addresses.findIndex(addr => addr.isDefault);
        
        if (defaultAddressIndex !== -1) {
            setSelectedAddressIndex(defaultAddressIndex);
            loadAddressIntoForm(addresses[defaultAddressIndex]);
        } else if (addresses.length > 0) {
            // If no default, select first address
            setSelectedAddressIndex(0);
            loadAddressIntoForm(addresses[0]);
        } else {
            // No saved addresses - enable manual entry
            setIsManualAddress(true);
        }

        // Load user profile email
        const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        if (userProfile.email) {
            setShippingDetails(prev => ({
                ...prev,
                email: userProfile.email
            }));
        }
    }, []);

    // ✅ NEW: Load selected address into form
    const loadAddressIntoForm = (address) => {
        setShippingDetails(prev => ({
            fullName: address.fullName,
            email: prev.email || '', // Keep existing email
            phone: address.phone,
            address: address.address,
            city: address.city,
            state: address.state,
            pincode: address.pincode
        }));
    };

    // ✅ NEW: Handle address selection change
    const handleAddressSelection = (e) => {
        const value = e.target.value;

        if (value === 'manual') {
            setIsManualAddress(true);
            setSelectedAddressIndex(null);
            // Clear form for manual entry (keep email)
            setShippingDetails(prev => ({
                fullName: '',
                email: prev.email,
                phone: '',
                address: '',
                city: '',
                state: '',
                pincode: ''
            }));
        } else {
            setIsManualAddress(false);
            const index = parseInt(value);
            setSelectedAddressIndex(index);
            loadAddressIntoForm(savedAddresses[index]);
        }
    };

    const getTotalPrice = () => {
        return wishlist.reduce((total, item) => {
            return total + (parseFloat(item.price) * item.quantity);
        }, 0).toFixed(2);
    };

    const getTotalItems = () => {
        return wishlist.reduce((total, item) => total + item.quantity, 0);
    };

    // Format card number with spaces
    const formatCardNumber = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        const matches = v.match(/\d{4,16}/g);
        const match = (matches && matches[0]) || '';
        const parts = [];

        for (let i = 0, len = match.length; i < len; i += 4) {
            parts.push(match.substring(i, i + 4));
        }

        if (parts.length) {
            return parts.join(' ');
        } else {
            return value;
        }
    };

    // Format expiry date MM/YY
    const formatExpiryDate = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        if (v.length >= 2) {
            return v.substring(0, 2) + '/' + v.substring(2, 4);
        }
        return v;
    };

    const handleCardInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'cardNumber') {
            setCardDetails({
                ...cardDetails,
                [name]: formatCardNumber(value).substring(0, 19) // Max 16 digits + 3 spaces
            });
        } else if (name === 'expiryDate') {
            setCardDetails({
                ...cardDetails,
                [name]: formatExpiryDate(value).substring(0, 5) // MM/YY
            });
        } else if (name === 'cvv') {
            setCardDetails({
                ...cardDetails,
                [name]: value.replace(/[^0-9]/gi, '').substring(0, 3)
            });
        } else {
            setCardDetails({
                ...cardDetails,
                [name]: value
            });
        }
    };

    const handleShippingInputChange = (e) => {
        const { name, value } = e.target;
        setShippingDetails({
            ...shippingDetails,
            [name]: value
        });
    };

    const validateShipping = () => {
        return (
            shippingDetails.fullName &&
            shippingDetails.email &&
            shippingDetails.phone &&
            shippingDetails.address &&
            shippingDetails.city &&
            shippingDetails.state &&
            shippingDetails.pincode
        );
    };

    const validateCard = () => {
        return (
            cardDetails.cardNumber.replace(/\s/g, '').length === 16 &&
            cardDetails.cardName &&
            cardDetails.expiryDate.length === 5 &&
            cardDetails.cvv.length === 3
        );
    };

    const handlePlaceOrder = () => {
        if (!validateShipping()) {
            alert('Please fill all shipping details');
            return;
        }

        if (paymentMethod === 'card' && !validateCard()) {
            alert('Please fill all card details correctly');
            return;
        }

        setLoading(true);

        // Simulate payment processing
        setTimeout(() => {
            const orderData = {
                orderId: 'ORD-' + Date.now(),
                items: wishlist,
                totalAmount: getTotalPrice(),
                totalItems: getTotalItems(),
                paymentMethod: paymentMethod === 'card' ? 'Card Payment' : 'Cash on Delivery',
                shippingDetails: shippingDetails,
                orderDate: new Date().toISOString(),
                status: 'Processing'
            };

            // Save to order history
            const existingOrders = JSON.parse(localStorage.getItem('orderHistory') || '[]');
            existingOrders.push(orderData);
            localStorage.setItem('orderHistory', JSON.stringify(existingOrders));

            // Clear wishlist
            localStorage.setItem('wishlist', JSON.stringify([]));

            setLoading(false);

            // Navigate to success page
            navigate('/order-success', { state: { orderData } });
        }, 2000);
    };

    return (
        <div className="checkout-container">
            <div className="checkout-wrapper">
                <h1 className="checkout-title">🛒 Checkout</h1>

                <div className="checkout-content">
                    {/* Left Column - Forms */}
                    <div className="checkout-left">
                        {/* Shipping Details */}
                        <div className="checkout-section">
                            <h2>📦 Shipping Details</h2>

                            {/* ✅ NEW: Address Selector Dropdown */}
                            {savedAddresses.length > 0 && (
                                <div className="address-selector-wrapper">
                                    <label htmlFor="address-select" className="address-selector-label">
                                        <strong>📍 Delivery Address:</strong>
                                    </label>
                                    <select
                                        id="address-select"
                                        className="address-select-dropdown"
                                        value={isManualAddress ? 'manual' : selectedAddressIndex}
                                        onChange={handleAddressSelection}
                                    >
                                        {savedAddresses.map((addr, index) => (
                                            <option key={index} value={index}>
                                                {addr.isDefault && '⭐ '}
                                                {addr.label} - {addr.fullName} ({addr.city})
                                            </option>
                                        ))}
                                        <option value="manual">➕ Enter New Address</option>
                                    </select>
                                </div>
                            )}

                            <div className="form-grid">
                                <input
                                    type="text"
                                    name="fullName"
                                    placeholder="Full Name *"
                                    value={shippingDetails.fullName}
                                    onChange={handleShippingInputChange}
                                    className={`form-input ${!isManualAddress && selectedAddressIndex !== null ? 'disabled-input' : ''}`}
                                    disabled={!isManualAddress && selectedAddressIndex !== null}
                                />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email *"
                                    value={shippingDetails.email}
                                    onChange={handleShippingInputChange}
                                    className="form-input"
                                />
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Phone Number *"
                                    value={shippingDetails.phone}
                                    onChange={handleShippingInputChange}
                                    className={`form-input ${!isManualAddress && selectedAddressIndex !== null ? 'disabled-input' : ''}`}
                                    disabled={!isManualAddress && selectedAddressIndex !== null}
                                />
                                <textarea
                                    name="address"
                                    placeholder="Full Address *"
                                    value={shippingDetails.address}
                                    onChange={handleShippingInputChange}
                                    className={`form-input ${!isManualAddress && selectedAddressIndex !== null ? 'disabled-input' : ''}`}
                                    rows="3"
                                    style={{ gridColumn: '1 / -1' }}
                                    disabled={!isManualAddress && selectedAddressIndex !== null}
                                />
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="City *"
                                    value={shippingDetails.city}
                                    onChange={handleShippingInputChange}
                                    className={`form-input ${!isManualAddress && selectedAddressIndex !== null ? 'disabled-input' : ''}`}
                                    disabled={!isManualAddress && selectedAddressIndex !== null}
                                />
                                <input
                                    type="text"
                                    name="state"
                                    placeholder="State *"
                                    value={shippingDetails.state}
                                    onChange={handleShippingInputChange}
                                    className={`form-input ${!isManualAddress && selectedAddressIndex !== null ? 'disabled-input' : ''}`}
                                    disabled={!isManualAddress && selectedAddressIndex !== null}
                                />
                                <input
                                    type="text"
                                    name="pincode"
                                    placeholder="Pincode *"
                                    value={shippingDetails.pincode}
                                    onChange={handleShippingInputChange}
                                    className={`form-input ${!isManualAddress && selectedAddressIndex !== null ? 'disabled-input' : ''}`}
                                    disabled={!isManualAddress && selectedAddressIndex !== null}
                                />
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="checkout-section">
                            <h2>💳 Payment Method</h2>
                            <div className="payment-methods">
                                <label className={`payment-option ${paymentMethod === 'card' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="card"
                                        checked={paymentMethod === 'card'}
                                        onChange={() => setPaymentMethod('card')}
                                    />
                                    <div className="payment-option-content">
                                        <span className="payment-icon">💳</span>
                                        <span>Debit/Credit Card</span>
                                    </div>
                                </label>

                                <label className={`payment-option ${paymentMethod === 'cod' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="cod"
                                        checked={paymentMethod === 'cod'}
                                        onChange={() => setPaymentMethod('cod')}
                                    />
                                    <div className="payment-option-content">
                                        <span className="payment-icon">💵</span>
                                        <span>Cash on Delivery</span>
                                    </div>
                                </label>
                            </div>

                            {/* Card Details Form */}
                            {paymentMethod === 'card' && (
                                <div className="card-details-form">
                                    <input
                                        type="text"
                                        name="cardNumber"
                                        placeholder="Card Number (16 digits)"
                                        value={cardDetails.cardNumber}
                                        onChange={handleCardInputChange}
                                        className="form-input"
                                    />
                                    <input
                                        type="text"
                                        name="cardName"
                                        placeholder="Cardholder Name"
                                        value={cardDetails.cardName}
                                        onChange={handleCardInputChange}
                                        className="form-input"
                                    />
                                    <div className="card-row">
                                        <input
                                            type="text"
                                            name="expiryDate"
                                            placeholder="MM/YY"
                                            value={cardDetails.expiryDate}
                                            onChange={handleCardInputChange}
                                            className="form-input"
                                        />
                                        <input
                                            type="text"
                                            name="cvv"
                                            placeholder="CVV"
                                            value={cardDetails.cvv}
                                            onChange={handleCardInputChange}
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Order Summary */}
                    <div className="checkout-right">
                        <div className="order-summary-sticky">
                            <h2>📋 Order Summary</h2>

                            <div className="order-items">
                                {wishlist.map((item, index) => (
                                    <div key={index} className="order-item">
                                        <div className="order-item-info">
                                            <span className="order-item-name">{item.name}</span>
                                            <span className="order-item-qty">Qty: {item.quantity}</span>
                                        </div>
                                        <span className="order-item-price">${(item.price * item.quantity).toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="order-totals">
                                <div className="order-total-row">
                                    <span>Subtotal ({getTotalItems()} items)</span>
                                    <span>${getTotalPrice()}</span>
                                </div>
                                <div className="order-total-row">
                                    <span>Shipping</span>
                                    <span className="free-shipping">Free</span>
                                </div>
                                <div className="order-total-row total">
                                    <span>Total</span>
                                    <span>${getTotalPrice()}</span>
                                </div>
                            </div>

                            <button
                                className="place-order-btn"
                                onClick={handlePlaceOrder}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <span>🎉</span>
                                        Place Order
                                    </>
                                )}
                            </button>

                            <button
                                className="back-to-wishlist-btn"
                                onClick={() => navigate('/wishlist')}
                            >
                                ← Back to Wishlist
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
